import { useState } from "react";
import { Reveal, CONTACT } from "./Reveal";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "@/components/ui/sonner";
import { Mail, Phone, MapPin, ArrowUpRight, Loader2 } from "lucide-react";

const SERVICES = [
  "Digital Marketing",
  "Online Advertising",
  "Social Media Management",
  "Branding",
  "Content Strategy",
  "Business Consulting",
];

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", company: "", service: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message sent", { description: "Thank you — we'll be in touch shortly." });
      setForm({ name: "", email: "", company: "", service: "", message: "" });
    } catch (err) {
      toast.error("Could not send", {
        description: formatApiErrorDetail(err.response?.data?.detail) || err.message,
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-white/20 py-3 text-base text-foreground placeholder:text-muted-foreground/60 focus:border-accent focus:outline-none transition-colors";

  return (
    <section id="contact" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 grid lg:grid-cols-2 gap-16">
        <div>
          <Reveal>
            <p className="text-xs tracking-[0.35em] uppercase text-accent mb-6">Contact</p>
            <h2 className="font-display text-4xl md:text-6xl lg:text-7xl tracking-tight leading-[0.98]">
              Let's make something
              <br />
              <span className="italic text-accent">worth talking about.</span>
            </h2>
          </Reveal>

          <div className="mt-14 space-y-6">
            {[
              { icon: Mail, label: "Email", value: CONTACT.email, href: `mailto:${CONTACT.email}` },
              { icon: Phone, label: "Phone", value: CONTACT.phone, href: `tel:${CONTACT.phone.replace(/\s/g, "")}` },
              { icon: MapPin, label: "Studio", value: CONTACT.address, href: null },
            ].map((c) => {
              const Icon = c.icon;
              const inner = (
                <div className="flex items-start gap-4 group">
                  <span className="grid place-items-center h-11 w-11 rounded-full border border-white/15 text-accent shrink-0 group-hover:bg-accent group-hover:text-accent-foreground transition-colors">
                    <Icon size={18} />
                  </span>
                  <div>
                    <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground">
                      {c.label}
                    </div>
                    <div className="text-base md:text-lg mt-1">{c.value}</div>
                  </div>
                </div>
              );
              return (
                <Reveal key={c.label}>
                  {c.href ? (
                    <a href={c.href} data-testid={`contact-info-${c.label.toLowerCase()}`}>
                      {inner}
                    </a>
                  ) : (
                    <div data-testid={`contact-info-${c.label.toLowerCase()}`}>{inner}</div>
                  )}
                </Reveal>
              );
            })}
          </div>
        </div>

        <Reveal delay={0.15}>
          <form
            onSubmit={submit}
            data-testid="contact-form"
            className="border border-white/10 rounded-sm p-6 md:p-10 bg-surface inner-glow"
          >
            <div className="grid sm:grid-cols-2 gap-6">
              <input
                data-testid="contact-name"
                required
                placeholder="Full name"
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
              <input
                data-testid="contact-email"
                required
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
            </div>
            <div className="grid sm:grid-cols-2 gap-6 mt-6">
              <input
                data-testid="contact-company"
                placeholder="Company (optional)"
                value={form.company}
                onChange={set("company")}
                className={inputCls}
              />
              <select
                data-testid="contact-service"
                value={form.service}
                onChange={set("service")}
                className={`${inputCls} appearance-none cursor-pointer`}
              >
                <option value="" className="bg-background">
                  Service of interest
                </option>
                {SERVICES.map((s) => (
                  <option key={s} value={s} className="bg-background">
                    {s}
                  </option>
                ))}
              </select>
            </div>
            <textarea
              data-testid="contact-message"
              required
              rows={4}
              placeholder="Tell us about your project"
              value={form.message}
              onChange={set("message")}
              className={`${inputCls} mt-6 resize-none`}
            />
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={loading}
              className="group mt-8 inline-flex items-center gap-3 rounded-full bg-primary text-primary-foreground pl-7 pr-3 py-3 text-sm font-medium disabled:opacity-60"
            >
              {loading ? "Sending..." : "Send message"}
              <span className="grid place-items-center h-9 w-9 rounded-full bg-accent text-accent-foreground transition-transform group-hover:rotate-45">
                {loading ? <Loader2 size={18} className="animate-spin" /> : <ArrowUpRight size={18} />}
              </span>
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
